import { Fragment } from 'react';
import {
  Link,
  createBrowserRouter,
  redirect,
  useRouteError,
} from 'react-router-dom';

import App from './App';
import Intro from './pages/Intro';
import Login from './pages/Login/Login';
import SignIn from './pages/Login/SignIn';
import ForgotPassword from './pages/Login/ForgotPassword';
import ResetPassword from './pages/Login/ResetPassword';
import SignUp from './pages/Login/SignUp';
import Teacher from './pages/Login/SignUp/Teacher';
import Student from './pages/Login/SignUp/Student';
import { NaverLoginCallback } from './pages/Login/SignIn/NaverLogin';

import {
  ACCOUNT_INFO,
  CANCEL_MEMBERSHIP,
  CHANGE_CARD,
  CLASSROOM,
  CLASS_ACTIVITY_INFO,
  CLASS_INFORMATION,
  EBOOK_ACTIVITY_INFO,
  FAQ,
  FEEDBACK,
  MEMBERSHIP,
  MEMBER,
  MY_ACTIVITY_INFO,
  NOTICE,
  PAYMENT_HISTORY,
  PRIVACY_POLICY,
  SETTINGS,
  SHARED_LINK_NOTIFICATION,
  STORAGE,
  STUDENT_REGISTRATION_REQUEST,
  TEACHER_INFORMATION,
  TERMS,
  WITHDRAWAL,
  WRITING_FEEDBACK,
  STUDENT_BULK_ADD,
} from './constants';
import { clearLocalStorage, getLocalStorage } from './utils/storage';

import Home from './pages/Home/Home';
import Main from './pages/Home/Main';
import MyLibrary from './pages/MyLibrary';
import SharedLibrary from './pages/SharedLibrary';
import MyDrive from './pages/MyDrive';
import Search from './pages/Search';
import Trash from './pages/Trash';
import Settings from './pages/Settings';
import SettingsMain from './pages/Settings/Main';
import Account from './pages/Settings/Account';
import Profile from './pages/Settings/Account/Profile';
import Storage from './pages/Settings/Storage';
import SharedLink from './pages/Settings/SharedLink';
import Notice from './pages/Settings/Notice';
import Faq from './pages/Settings/Faq';
import Feedback from './pages/Settings/Feedback/Feedback';
import Writing from './pages/Settings/Feedback/Writing';
import Terms from './pages/Settings/Terms';
import InactiveAccount from './pages/Login/InactiveAccount';
import DisabledAccount from './pages/Login/DisabledAccount';
import Recovery from './pages/Login/Recovery';
import Contact from './pages/Login/Contact';
import Classroom from './pages/Classroom';
import ClassMain from './pages/Classroom/Main';
import ClassInfo from './pages/Classroom/ClassInfo';
import TeacherInfo from './pages/Classroom/Teacher';
import ShareLink from './pages/ShareLink';
import MyActivityInfo from './pages/Classroom/MyActivityInfo';
import StudentRegistration from './pages/Settings/StudentRegistration';
import ClassActivityInfo from './pages/Classroom/ClassActivityInfo';
import StudentActivityInfo from './pages/Classroom/StudentActivityInfo';
import NiceAuth from './pages/Login/SignUp/NiceAuth';
import Membership from './pages/Settings/Membership';
import CancelMembership from './pages/Settings/CancelMembership';
import ChangeCard from './pages/Settings/ChangeCard';
import PaymentHistory from './pages/Settings/PaymentHistory';
import DeleteAccount from './pages/Settings/Account/DeleteAccount';
import EbookActivityInfo from './pages/Classroom/EbookActivityInfo';
import StudentActivity from './pages/Classroom/EbookActivityInfo/StudentActivity';
import {
  StudentBulkAdd,
  StudentBulkAddRequest,
} from './pages/Classroom/StudentBulkAdd';

function ErrorBoundary() {
  const error = useRouteError();
  console.error('ErrorBoundary : ', error);
  clearLocalStorage();
  return <Login />;
}

const redirectHome = () => {
  const isLoggedIn = getLocalStorage('loggedIn');
  const lastPath = location.pathname.split('/').pop();

  if (+isLoggedIn && lastPath !== 'contact' && lastPath !== 'account') {
    return redirect('/home');
  } else {
    return null;
  }
};

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: '/',
          element: <Intro />,
          loader: () => redirectHome(),
          children: [],
        },
        {
          path: 'nice-auth',
          element: <NiceAuth />,
        },
        {
          path: 'login',
          element: <Login />,
          loader: () => redirectHome(),
          children: [
            { path: '', element: <SignIn /> },
            { path: 'forgot-password', element: <ForgotPassword /> },
            { path: 'reset-password', element: <ResetPassword /> },
            {
              path: 'signup',
              element: <SignUp />,
            },
            { path: 'signup/teacher', element: <Teacher /> },
            { path: 'signup/student', element: <Student /> },
            { path: 'signup/minor', element: <Student /> },
            {
              path: 'inactive-account',
              element: <InactiveAccount />,
            },
            {
              path: 'disabled-account',
              element: <DisabledAccount />,
            },
            {
              path: 'recovery',
              element: <Recovery />,
            },
            {
              path: 'contact',
              element: <Contact />,
            },
          ],
        },
        { path: 'login/test', element: <NaverLoginCallback /> },
        {
          path: 'home',
          element: <Home />,
          children: [
            { path: '', element: <Main /> },
            { path: 'library', element: <MyLibrary /> },
            { path: 'share', element: <SharedLibrary /> },
            {
              path: 'mydrive',
              element: <MyDrive />,
              children: [
                {
                  path: 'folders/:folderId',
                  element: <MyDrive />,
                },
              ],
            },
            { path: 'search', element: <Search /> },
            { path: 'trash', element: <Trash /> },
            {
              path: 'classroom',
              element: <Classroom />,
              children: [
                {
                  path: '',
                  element: <ClassMain />,
                  handle: {
                    crumb: () => (
                      <span>
                        <h1>{CLASSROOM}</h1>
                      </span>
                    ),
                  },
                },
                {
                  path: 'my-activity-info',
                  element: <MyActivityInfo />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{CLASSROOM}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{MY_ACTIVITY_INFO}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'class-activity-info',
                  element: <ClassActivityInfo />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{CLASSROOM}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{CLASS_ACTIVITY_INFO}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'ebook-activity-info',
                  element: <EbookActivityInfo />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{CLASSROOM}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{EBOOK_ACTIVITY_INFO}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'student-activity',
                  element: <StudentActivity />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{CLASSROOM}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{EBOOK_ACTIVITY_INFO}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'student-activity-info',
                  element: <StudentActivityInfo />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{CLASSROOM}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{CLASS_ACTIVITY_INFO}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'class',
                  element: <ClassInfo />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{CLASSROOM}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{CLASS_INFORMATION}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'teacher',
                  element: <TeacherInfo />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{CLASSROOM}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{TEACHER_INFORMATION}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'student-bulk-add',
                  children: [
                    {
                      path: '',
                      element: <StudentBulkAdd />,
                    },
                    {
                      path: 'request',
                      element: <StudentBulkAddRequest />,
                    },
                  ],
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{CLASSROOM}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{STUDENT_BULK_ADD}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
              ],
            },
            {
              path: 'settings',
              element: <Settings />,
              children: [
                {
                  path: '',
                  element: <SettingsMain />,
                  handle: {
                    crumb: () => (
                      <span>
                        <h1>{SETTINGS}</h1>
                      </span>
                    ),
                  },
                },
                {
                  path: 'account',
                  element: <Account />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{ACCOUNT_INFO}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'profile',
                  element: <Profile />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{ACCOUNT_INFO}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'delete',
                  element: <DeleteAccount />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <Link to={'profile'}>{ACCOUNT_INFO}</Link>
                            <span>{` > `}</span>
                            <span>
                              {MEMBER} {WITHDRAWAL}
                            </span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'membership',
                  element: <Membership />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{MEMBERSHIP}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'cancel-membership',
                  element: <CancelMembership />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <Link to={'./membership'}>{MEMBERSHIP}</Link>
                            <span>{` > `}</span>
                            <span>{CANCEL_MEMBERSHIP}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'change-card',
                  element: <ChangeCard />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <Link to={'./membership'}>{MEMBERSHIP}</Link>
                            <span>{` > `}</span>
                            <span>{CHANGE_CARD}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'payment-history',
                  element: <PaymentHistory />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <Link to={'./membership'}>{MEMBERSHIP}</Link>
                            <span>{` > `}</span>
                            <span>{PAYMENT_HISTORY}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'storage',
                  element: <Storage />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{STORAGE}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'sharedLink',
                  element: <SharedLink />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{SHARED_LINK_NOTIFICATION}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'student-registration-request',
                  element: <StudentRegistration />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{STUDENT_REGISTRATION_REQUEST}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'notice',
                  element: <Notice />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{NOTICE}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'contact',
                  element: <Contact />,
                },
                {
                  path: 'faq',
                  element: <Faq />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{FAQ}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'feedback',
                  element: <Feedback />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{FEEDBACK}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'writing-feedback',
                  element: <Writing />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <Link to={name}>{FEEDBACK}</Link>
                            <span>{` > `}</span>
                            <span>{WRITING_FEEDBACK}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'terms',
                  element: <Terms />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{TERMS}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
                {
                  path: 'privacy-policy',
                  element: <Terms />,
                  handle: {
                    crumb: (data: string[]) => (
                      <h1>
                        <Link to="">{SETTINGS}</Link>
                        {data.map((name) => (
                          <Fragment key={name}>
                            <span>{` > `}</span>
                            <span>{PRIVACY_POLICY}</span>
                          </Fragment>
                        ))}
                      </h1>
                    ),
                  },
                },
              ],
            },
          ],
        },
        { path: 'share', element: <ShareLink /> },
      ],
    },
  ],
  {
    basename: import.meta.env.BASE_URL,
    future: {
      v7_relativeSplatPath: true,
    },
  }
);

export default router;
